// The VIDEO (reel) edit bottom-sheet body — the mobile counterpart of the desktop
// ReelTimelineDock + stage, CapCut-shaped for a phone:
//   header (undo/redo · render CTA) → stage (playback-true canvas, touch layer editing) →
//   transport → MobileReelTimeline (scroll-to-scrub strip) → contextual toolbar → detail sheets.
// The detail sheets reuse the SAME inspector contexts the desktop timeline shows in its left
// column (ClipContext/VoContext/CaptionsContext/MusicContext/LayerContext) — one source of
// truth for every beat/deck control. All state flows through useVideoTimeline(); edits autosave
// and NEVER auto-render (Render is the only master trigger), exactly like desktop.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Clapperboard,
  Clock,
  ImageIcon,
  Loader2,
  Mic,
  Music,
  Pause,
  Play,
  Redo2,
  Shapes,
  Sparkles,
  Subtitles,
  Type,
  Undo2,
} from 'lucide-react'
import { cn } from 'lib/utils'
import { MobileBottomSheet, useExitMount } from '../../components/MobileBottomSheet'
import { CompositionCanvas } from '../CompositionCanvas'
import { SelectionOverlay } from './SelectionOverlay'
import { playheadTrueView } from './playhead-view'
import { ReelTimecode } from './PlayheadWidgets'
import { MobileLayerToolbar, TBTN_CLASS, TBtn } from './MobileLayerToolbar'
import { ReplacePhotoPopover } from './ReplacePhotoPopover'
import { MobileReelTimeline } from './MobileReelTimeline'
import { BeatBackgroundFill, ReelPlaybackStage } from './VideoTimeline'
import {
  CaptionsContext,
  ClipContext,
  GeneratingVeil,
  LayerContext,
  MusicContext,
  RENDER_PHASE_LABEL,
  VoContext,
  hasPlannedBeats,
} from './ReelTimeline'
import { useComposerEditor, useReelPlayhead, useVideoTimeline } from './CompositionEditorProvider'

const INSET = 20 // breathing room around the fit-to-screen stage

type SheetKind = 'beat' | 'vo' | 'captions' | 'music' | 'timing' | null

export function MobileVideoEditor({ onAskAIBeat }: { onAskAIBeat?: (index: number) => void }) {
  const ctx = useComposerEditor()
  const timeline = useVideoTimeline()
  const [sheet, setSheet] = useState<SheetKind>(null)
  // The Timing sheet's clip id is CAPTURED when the sheet opens (not the live canvas
  // selection): applying an anim/window rebuilds the doc and can drop the selection, and the
  // sheet must keep editing the same clip through that — the same reason the desktop
  // inspector keys on its own selectedClipId.
  const [timingClipId, setTimingClipId] = useState<string | null>(null)

  // Closing the edit sheet must stop the preview: the provider (and its rAF clock + <audio>
  // elements) lives ABOVE this sheet and would keep playing into the composer home.
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline
  useEffect(() => () => timelineRef.current?.pause(), [])

  if (!ctx || !timeline) return null
  const { editor } = ctx
  const beat = timeline.beats[timeline.activeIndex]

  const askBeat = onAskAIBeat
    ? (index: number) => {
        setSheet(null)
        onAskAIBeat(index)
      }
    : undefined

  const draft = hasPlannedBeats(timeline)
  const hydrating = timeline.hydration != null

  return (
    <div data-mobile-editor className="relative flex h-full flex-col overflow-hidden bg-bg-2">
      {/* Header — undo/redo · status · the primary CTA (hydration / bring-to-life / render). */}
      <div className="flex flex-shrink-0 items-center gap-1 px-2 py-1.5">
        <button
          type="button"
          onClick={editor.undo}
          disabled={!editor.canUndo}
          aria-label="Undo"
          className="flex h-9 w-9 items-center justify-center rounded-full text-paper-dim active:bg-bg-3 disabled:opacity-30"
        >
          <Undo2 size={18} />
        </button>
        <button
          type="button"
          onClick={editor.redo}
          disabled={!editor.canRedo}
          aria-label="Redo"
          className="flex h-9 w-9 items-center justify-center rounded-full text-paper-dim active:bg-bg-3 disabled:opacity-30"
        >
          <Redo2 size={18} />
        </button>
        <span className="flex-1" />
        {timeline.editedSinceRender && !timeline.rendering && (
          <span
            className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/90"
            title="Your edits aren't in the score or the video yet. Render to update."
            aria-label="Edits pending render"
          />
        )}
        {hydrating ? (
          <span className="flex h-8 items-center gap-1.5 rounded-full border border-acid/50 bg-acid/10 px-3 text-[11px] font-semibold text-paper">
            <Loader2 size={12} className="animate-spin" />
            Filming · {timeline.hydration!.done}/{timeline.hydration!.total}
          </span>
        ) : draft ? (
          <button
            type="button"
            onClick={() => void timeline.bringToLife()}
            className="flex h-8 items-center gap-1.5 rounded-full bg-acid px-3.5 text-[11px] font-semibold text-bg"
          >
            <Sparkles size={12} />
            Bring it to life
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void timeline.render()}
            disabled={timeline.rendering}
            className={cn(
              'flex h-8 items-center gap-1.5 rounded-full bg-acid px-3.5 text-[11px] font-semibold text-bg',
              timeline.rendering && 'opacity-60'
            )}
          >
            {timeline.rendering ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Sparkles size={12} />
            )}
            {timeline.rendering
              ? `Rendering${timeline.renderPhase && RENDER_PHASE_LABEL[timeline.renderPhase] ? `: ${RENDER_PHASE_LABEL[timeline.renderPhase]}` : ''}…`
              : 'Render'}
          </button>
        )}
      </div>
      {timeline.renderError && (
        <span className="px-3 pb-1 text-[11px] text-red-400" role="alert">
          {timeline.renderError}
        </span>
      )}

      {/* Stage — playing: the live playback view (tap pauses). Paused: the ACTIVE beat as the
          playhead-true editable canvas over its motion background. */}
      <MobileVideoStage />

      {/* Transport. */}
      <div className="flex flex-shrink-0 items-center justify-center gap-2.5 px-3 py-1.5">
        <button
          type="button"
          onClick={() => (timeline.playing ? timeline.pause() : timeline.play())}
          aria-label={timeline.playing ? 'Pause' : 'Play'}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-bg-3 text-paper"
        >
          {timeline.playing ? <Pause size={17} /> : <Play size={17} className="translate-x-px" />}
        </button>
        <ReelTimecode
          timeline={timeline}
          total={timeline.totalDuration}
          className="text-[11px] text-paper-faint tabular-nums"
        />
        {timeline.voicingBeatIds.size > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-paper-dim">
            <Loader2 size={11} className="animate-spin" /> Voice…
          </span>
        )}
      </div>

      {/* The scroll-to-scrub timeline strip. */}
      <MobileReelTimeline />

      {/* Contextual toolbar — a layer selected on the canvas gets the full layer toolset
          (+ clip Timing); otherwise the BEAT tools. */}
      {editor.selected ? (
        <MobileLayerToolbar
          leading={
            <TBtn
              icon={<Clock size={18} />}
              label="Timing"
              onClick={() => {
                setTimingClipId(editor.selectedId ?? null)
                setSheet('timing')
              }}
            />
          }
        />
      ) : (
        <div className="border-t border-line bg-bg-2 pb-[env(safe-area-inset-bottom)]">
          <div className="flex items-center gap-1 overflow-x-auto px-2 py-1.5">
            <TBtn
              icon={<Clapperboard size={18} />}
              label={`Beat ${timeline.activeIndex + 1}`}
              onClick={() => setSheet('beat')}
            />
            <TBtn icon={<Mic size={18} />} label="Script" onClick={() => setSheet('vo')} />
            <TBtn
              icon={<Subtitles size={18} />}
              label="Captions"
              onClick={() => setSheet('captions')}
            />
            <TBtn icon={<Music size={18} />} label="Music" onClick={() => setSheet('music')} />
            <TBtn icon={<Type size={18} />} label="Text" onClick={() => editor.addText()} />
            <TBtn icon={<Shapes size={18} />} label="Shape" onClick={() => editor.addShape()} />
            <ReplacePhotoPopover
              title="Add photo"
              onPick={attachmentId => editor.addImage(attachmentId)}
            >
              <button type="button" className={TBTN_CLASS}>
                <ImageIcon size={18} />
                <span>Photo</span>
              </button>
            </ReplacePhotoPopover>
            {askBeat && (
              <TBtn
                icon={<Sparkles size={18} />}
                label="Ask Taka"
                onClick={() => askBeat(timeline.activeIndex)}
              />
            )}
          </div>
        </div>
      )}

      {/* Detail sheets — the desktop inspector contexts, one at a time, stacked ABOVE this
          sheet (zIndex raised). */}
      <DetailSheet
        open={sheet === 'beat'}
        onClose={() => setSheet(null)}
        title={beat ? `Beat ${timeline.activeIndex + 1}` : 'Beat'}
      >
        {beat && (
          <ClipContext
            timeline={timeline}
            index={timeline.activeIndex}
            beat={beat}
            onAskAIBeat={askBeat}
          />
        )}
      </DetailSheet>
      <DetailSheet
        open={sheet === 'vo'}
        onClose={() => setSheet(null)}
        title="Voiceover"
        height="full"
      >
        {beat && (
          <VoContext
            key={beat.id}
            timeline={timeline}
            index={timeline.activeIndex}
            beat={beat}
            autoFocusScript={false}
          />
        )}
      </DetailSheet>
      <DetailSheet open={sheet === 'captions'} onClose={() => setSheet(null)} title="Captions">
        <CaptionsContext timeline={timeline} />
      </DetailSheet>
      <DetailSheet open={sheet === 'music'} onClose={() => setSheet(null)} title="Music">
        <MusicContext timeline={timeline} />
      </DetailSheet>
      <DetailSheet open={sheet === 'timing'} onClose={() => setSheet(null)} title="On screen">
        <LayerContext
          timeline={timeline}
          selectedClipId={timingClipId}
          onClipGone={() => setSheet(null)}
        />
      </DetailSheet>
    </div>
  )
}

function DetailSheet({
  open,
  onClose,
  title,
  height = 'half',
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  height?: 'full' | 'half'
  children: React.ReactNode
}) {
  const mounted = useExitMount(open)
  return (
    <MobileBottomSheet open={open} onClose={onClose} title={title} height={height} zIndex="raised">
      {mounted && <div className="px-4 py-4">{children}</div>}
    </MobileBottomSheet>
  )
}

/** The mobile stage. Paused: the active beat's motion background with the PLAYHEAD-TRUE
 *  editable overlay on top — every clip whose window touches the beat is a touch-editable
 *  layer, gated to its clip window at the playhead (same display synthesis as the desktop
 *  ComposerCanvasMedia, minus the hover chrome). Playing: the shared ReelPlaybackStage. */
function MobileVideoStage() {
  const ctx = useComposerEditor()
  const timeline = useVideoTimeline()
  // Subscribed above every early return (rules-of-hooks).
  const playhead = useReelPlayhead(timeline)
  const areaRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ w: number; h: number } | null>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const el = areaRef.current
    if (!el) return
    const update = () => setBox({ w: el.clientWidth, h: el.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (!ctx || !timeline) return null
  const { editor } = ctx
  const activeBeat = timeline.beats[timeline.activeIndex]

  // Playhead-true paused canvas (shared with the desktop stage — see playhead-view.ts).
  // Skipped entirely while playing: ReelPlaybackStage owns the frame then; synthesizing an
  // unused doc per frame would be pure waste.
  const view = timeline.playing
    ? { displayComp: editor.comp }
    : playheadTrueView(editor, timeline, playhead)
  const { displayComp, beatTime, hitDisabledIds } = view

  return (
    <div
      ref={areaRef}
      className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
    >
      <div
        className="grid-bg pointer-events-none absolute inset-0"
        data-grid-bg="emphasized"
        aria-hidden
      />
      {timeline.playing ? (
        // Height-constrained 9:16 wrapper: ReelPlaybackStage is width-driven (w-full), so on a
        // short phone stage the wrapper's aspect sets the width from the available HEIGHT.
        <div className="relative mx-auto aspect-[9/16] h-full max-w-full py-1">
          <ReelPlaybackStage />
        </div>
      ) : (
        box &&
        box.w > 0 &&
        box.h > 0 && (
          <div
            ref={stageRef}
            data-canvas-visual
            className="relative w-fit overflow-hidden rounded-[14px] bg-black"
          >
            {activeBeat && <BeatBackgroundFill bg={activeBeat.background} stage />}
            <div className="relative">
              <CompositionCanvas
                composition={displayComp}
                beatTime={beatTime}
                transparent
                displayWidth={Math.max(1, box.w - INSET)}
                maxHeight={Math.max(1, box.h - INSET)}
                onScaleChange={i => setScale(i.scale)}
                suppressLayerId={editor.editingId ?? undefined}
              >
                <SelectionOverlay
                  editor={editor}
                  scale={scale}
                  containerRef={stageRef}
                  touch
                  hitDisabledIds={hitDisabledIds}
                />
              </CompositionCanvas>
            </div>
            {activeBeat && timeline.generatingBeatIds.has(activeBeat.id) && (
              <GeneratingVeil label="Filming…" />
            )}
          </div>
        )
      )}
    </div>
  )
}
